<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Matching &amp; Assignment Rules
          </div>
          <div class="header-subtitle">
            Applies when the Matching Engine performs a match request.
          </div>
        </template>
      </EsHeader>
      <div class="icon-right">
        <a
          @click="editPanel()"
          v-if="!editCurrentPanel"
        >
          <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
        </a>
      </div>
    </div>
    <div class="content" style="padding : 0.5rem; !important">
      <table>
        <tbody>
          <tr>
            <th class="category" colspan="2" scope="colgroup">
              SNAP Match Rules
            </th>
          </tr>
          <tr>
            <th class="rule" scope="col">
              Rule Name
            </th>
            <th class="rule" style="font-weight: 600" scope="col">
              Rule Value(s)
            </th>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Match Age Difference
            </th>
            <td>
              <div class="control">
                <input
                  class="input is-pulled-right"
                  :class="{ 'is-static': !editCurrentPanel }"
                  @keypress="isWholeNumbers($event)"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="snapMatchAgeDiff"
                  :aria-label="snapMatchAgeDiff"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Match SSNs Different
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="snapMatchSsn = option.description"
                    :class="{ 'is-active': snapMatchSsn === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="snapMatchSsn"
                  :readonly="!editCurrentPanel"
                  :aria-label="snapMatchSsn"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - No Match SSNs Same
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="snapNoMatchSsn = option.description"
                    :class="{ 'is-active': snapNoMatchSsn === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="snapNoMatchSsn"
                  :readonly="!editCurrentPanel"
                  :aria-label="snapNoMatchSsn"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Twins Rule
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="snapTwinRule = option.description"
                    :class="{ 'is-active': snapTwinRule === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="snapTwinRule"
                  :readonly="!editCurrentPanel"
                  :aria-label="snapTwinRule"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              {{ properties['SYSTEM_LABEL-NEARMATCH'] }} Threshold (Lower/Upper)
            </th>
            <td>
              <div class="control">
                <input
                  class="input has-text-right"
                  :class="!editCurrentPanel ? ['is-static', 'input-display'] : ''"
                  @keypress="isWholeNumbers($event)"
                  @keyup.prevent="validateField('snapMatchNMThres')"
                  maxlength="3"
                  :readonly="!editCurrentPanel"
                  style="max-width: 50px"
                  type="text"
                  v-model="snapNMLowThres"
                  :aria-label="snapNMLowThres"
                >
                <span> / </span>
                <input
                  class="input"
                  :class="!editCurrentPanel ? ['is-static', 'input-display'] : ''"
                  @keypress="isWholeNumbers($event)"
                  @keyup.prevent="validateField('snapMatchNMThres')"
                  maxlength="3"
                  :readonly="!editCurrentPanel"
                  style="max-width: 50px"
                  type="text"
                  v-model="snapNMUpThres"
                  :aria-label="snapNMUpThres"
                >
              </div>
              <span v-if="hasErrSnapMatchNM" class="help is-danger">
                {{ msgErrSnapMatchNM }}
              </span>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Multiple Matches - Automatically Assign ID to 100% Match Candidate
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="snapMultiNMAutoAssign = option.description"
                    :class="{ 'is-active': snapMultiNMAutoAssign === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="snapMultiNMAutoAssign"
                  :readonly="!editCurrentPanel"
                  :aria-label="snapMultiNMAutoAssign"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="category" colspan="2" scope="colgroup">
              TANF Match Rules
            </th>
          </tr>
          <tr>
            <th class="rule" scope="col">
              Rule Name
            </th>
            <th class="rule" scope="col">
              Rule Value(s)
            </th>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Match Age Difference
            </th>
            <td>
              <div class="control">
                <input
                  class="input is-pulled-right"
                  :class="{ 'is-static': !editCurrentPanel }"
                  @keypress="isWholeNumbers($event)"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="tanfMatchAgeDiff"
                  :aria-label="tanfMatchAgeDiff"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Match SSNs Different
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="tanfMatchSsn = option.description"
                    :class="{ 'is-active': tanfMatchSsn === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="tanfMatchSsn"
                  :readonly="!editCurrentPanel"
                  :aria-label="tanfMatchSsn"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - No Match SSNs Same
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="tanfNoMatchSsn = option.description"
                    :class="{ 'is-active': tanfNoMatchSsn === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="tanfNoMatchSsn"
                  :readonly="!editCurrentPanel"
                  :aria-label="tanfNoMatchSsn"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Force {{ properties['SYSTEM_LABEL-NEARMATCH'] }} - Twins Rule
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="tanfTwinRule = option.description"
                    :class="{ 'is-active': tanfTwinRule === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="tanfTwinRule"
                  :readonly="!editCurrentPanel"
                  :aria-label="tanfTwinRule"
                >
              </div>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              {{ properties['SYSTEM_LABEL-NEARMATCH'] }} Threshold (Lower/Upper)
            </th>
            <td>
              <div class="control">
                <input
                  class="input has-text-right"
                  :class="!editCurrentPanel ? ['is-static', 'input-display'] : ''"
                  @keypress="isWholeNumbers($event)"
                  @keyup.prevent="validateField('tanfMatchNMThres')"
                  maxlength="3"
                  :readonly="!editCurrentPanel"
                  style="max-width: 50px"
                  type="text"
                  v-model="tanfNMLowThres"
                  :aria-label="tanfNMLowThres"
                >
                <span> / </span>
                <input
                  class="input"
                  :class="!editCurrentPanel ? ['is-static', 'input-display'] : ''"
                  @keypress="isWholeNumbers($event)"
                  @keyup.prevent="validateField('tanfMatchNMThres')"
                  maxlength="3"
                  :readonly="!editCurrentPanel"
                  style="max-width: 50px"
                  type="text"
                  v-model="tanfNMUpThres"
                  :aria-label="tanfNMUpThres"
                >
              </div>
              <span v-if="hasErrTanfMatchNM" class="help is-danger">
                {{ msgErrTanfMatchNM }}
              </span>
            </td>
          </tr>
          <tr>
            <th class="th-normal" scope="row">
              Multiple Matches - Automatically Assign ID to 100% Match Candidate
            </th>
            <td>
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="tanfMultiNMAutoAssign = option.description"
                    :class="{ 'is-active': tanfMultiNMAutoAssign === option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="tanfMultiNMAutoAssign"
                  :readonly="!editCurrentPanel"
                  :aria-label="tanfMultiNMAutoAssign"
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsHeader from '@/components/EsHeader.vue';
import NotificationManager from '@/mixins/NotificationManager';

library.add(
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
  ],
  components: {
    EsHeader,
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  created() {
    this.enabledDisabled = [
      {
        code: true,
        description: 'Enabled',
      },
      {
        code: false,
        description: 'Disabled',
      },
    ];
  },
  data() {
    return this.initialData();
  },
  methods: {
    cancelPanel() {
      this.resetData();
      this.$emit('allowEditOtherPanels');
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    initialData() {
      return {
        editCurrentPanel: false,

        // Snap Match Variables
        snapMatchAgeDiff: 50,
        snapMatchSsn: 'Disabled',
        snapNoMatchSsn: 'Disabled',
        snapTwinRule: 'Disabled',
        snapNMLowThres: 88,
        snapNMUpThres: 93,
        snapMultiNMAutoAssign: 'Disabled',
        hasErrSnapMatchNM: false,
        msgErrSnapMatchNM: null,

        // TanF Match Variables
        tanfMatchAgeDiff: 50,
        tanfMatchSsn: 'Disabled',
        tanfNoMatchSsn: 'Disabled',
        tanfTwinRule: 'Disabled',
        tanfNMLowThres: 88,
        tanfNMUpThres: 93,
        tanfMultiNMAutoAssign: 'Disabled',
        hasErrTanfMatchNM: false,
        msgErrTanfMatchNM: null,
      };
    },
    isWholeNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    resetData() {
      Object.assign(this.$data, this.initialData());
    },
    updatePanel() {
      const err = this.hasErrSnapMatchNM || this.hasErrTanfMatchNM;
      if (err) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else {
        this.editCurrentPanel = false;
        this.showNotification('success', ['The \'Matching & Assignment Rules\' have been updated successfully.']);
        this.$emit('allowEditOtherPanels');
      }
    },
    validateField(field) {
      if (field === 'snapMatchNMThres') {
        const lowThres = parseInt(this.snapNMLowThres, 10);
        const upThres = parseInt(this.snapNMUpThres, 10);
        if (lowThres < 0 || lowThres > 100 || upThres < 0 || upThres > 100) {
          this.hasErrSnapMatchNM = true;
          this.msgErrSnapMatchNM = 'Values must be between 0 and 100.';
        } else if (lowThres >= upThres) {
          this.hasErrSnapMatchNM = true;
          this.msgErrSnapMatchNM = 'Lower Threshold must be less than Upper Threshold.';
        } else {
          this.hasErrSnapMatchNM = false;
          this.msgErrSnapMatchNM = null;
        }
      }
      if (field === 'tanfMatchNMThres') {
        const lowThres = parseInt(this.tanfNMLowThres, 10);
        const upThres = parseInt(this.tanfNMUpThres, 10);
        if (lowThres < 0 || lowThres > 100 || upThres < 0 || upThres > 100) {
          this.hasErrTanfMatchNM = true;
          this.msgErrTanfMatchNM = 'Values must be between 0 and 100.';
        } else if (lowThres >= upThres) {
          this.hasErrTanfMatchNM = true;
          this.msgErrTanfMatchNM = 'Lower Threshold must be less than Upper Threshold.';
        } else {
          this.hasErrTanfMatchNM = false;
          this.msgErrTanfMatchNM = null;
        }
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  span
    vertical-align: -2px
  .category
    padding-top: 1.5rem
  .header
    display: flex
    align-items: center
  .icon-right
    padding-right: 1rem
  .input-display
    max-width: 25px !important
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .th-normal
    font-weight: 400
    vertical-align: middle
</style>
